// src/pages/BuildListPage.jsx
// Filter-based provider list builder.
// City-state tag box (multi-market), two-level specialty picker with tag box,
// and financial filters (min payments, min ownership value, min research 2025).

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import { useSubscription } from "../context/SubscriptionContext";
import { formatCurrency } from "../utils/cmsApi";
import { apiFetch } from "../utils/apiClient";

// ── US States ─────────────────────────────────────────────────────────────────
const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],
  ["CA","California"],["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],
  ["DC","District of Columbia"],["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],
  ["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],
  ["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],
  ["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],
  ["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],
  ["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],
  ["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],
  ["VT","Vermont"],["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],
  ["WI","Wisconsin"],["WY","Wyoming"],
];

// ── Specialty groups — two-level: group → classifications ─────────────────────
const SPECIALTY_GROUPS = [
  {
    group: "Physicians — Medical",
    classifications: [
      "Allergy & Immunology","Anesthesiology","Clinical Pharmacology",
      "Dermatology","Electrodiagnostic Medicine","Emergency Medicine",
      "Family Medicine","General Practice","Hospitalist","Independent Medical Examiner",
      "Integrative Medicine","Internal Medicine","Legal Medicine","Medical Genetics",
      "Neuromusculoskeletal Medicine & OMM","Neuromusculoskeletal Medicine, Sports Medicine",
      "Nuclear Medicine","Obstetrics & Gynecology","Ophthalmology","Pain Medicine",
      "Pathology","Pediatrics","Phlebology","Physical Medicine & Rehabilitation",
      "Preventive Medicine","Psychiatry & Neurology","Radiology","Urology",
    ],
  },
  {
    group: "Physicians — Surgical",
    classifications: [
      "Colon & Rectal Surgery","Neurological Surgery","Oral & Maxillofacial Surgery",
      "Orthopaedic Surgery","Otolaryngology","Plastic Surgery","Surgery",
      "Thoracic Surgery (Cardiothoracic Vascular Surgery)","Transplant Surgery",
    ],
  },
  {
    group: "Advanced Practice Providers",
    classifications: [
      "Advanced Practice Midwife","Anesthesiologist Assistant","Clinical Nurse Specialist",
      "Nurse Anesthetist, Certified Registered","Nurse Practitioner","Physician Assistant",
    ],
  },
  {
    group: "Nursing",
    classifications: [
      "Licensed Practical Nurse","Licensed Psychiatric Technician",
      "Licensed Vocational Nurse","Registered Nurse",
    ],
  },
  {
    group: "Behavioral Health",
    classifications: [
      "Assistant Behavior Analyst","Behavior Analyst","Behavior Technician",
      "Clinical Neuropsychologist","Counselor","Drama Therapist",
      "Marriage & Family Therapist","Poetry Therapist","Psychoanalyst","Psychologist",
      "Social Worker",
    ],
  },
  {
    group: "Dental",
    classifications: [
      "Advanced Practice Dental Therapist","Dental Assistant","Dental Hygienist",
      "Dental Laboratory Technician","Dental Therapist","Dentist","Denturist",
    ],
  },
  {
    group: "Chiropractic",
    classifications: ["Chiropractor"],
  },
  {
    group: "Podiatry",
    classifications: ["Assistant, Podiatric","Podiatrist"],
  },
  {
    group: "Eye & Vision",
    classifications: ["Optometrist","Technician/Technologist"],
  },
  {
    group: "Pharmacy",
    classifications: ["Pharmacist","Pharmacy Technician"],
  },
  {
    group: "Rehabilitation & Therapy",
    classifications: [
      "Anaplastologist","Art Therapist","Audiologist","Audiologist-Hearing Aid Fitter",
      "Clinical Exercise Physiologist","Dance Therapist","Developmental Therapist",
      "Kinesiotherapist","Massage Therapist","Music Therapist",
      "Occupational Therapist","Occupational Therapy Assistant","Orthotist",
      "Physical Therapist","Physical Therapy Assistant","Prosthetist",
      "Pulmonary Function Technologist","Recreation Therapist",
      "Respiratory Therapist, Certified","Respiratory Therapist, Registered",
      "Speech-Language Pathologist",
    ],
  },
  {
    group: "Dietary & Nutrition",
    classifications: [
      "Dietary Manager","Dietetic Technician, Registered","Dietitian, Registered","Nutritionist",
    ],
  },
  {
    group: "Other Providers",
    classifications: [
      "Acupuncturist","Case Manager/Care Coordinator","Community Health Worker",
      "Genetic Counselor, MS","Health Educator","Homeopath","Midwife",
      "Naturopath","Peer Specialist","Veterinarian",
    ],
  },
];

// Flat list of all classifications for lookup
const ALL_CLASSIFICATIONS = SPECIALTY_GROUPS.flatMap((g) =>
  g.classifications.map((c) => ({ group: g.group, classification: c }))
);

function titleCase(str = "") {
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── City-State tag box with search-as-you-type ────────────────────────────────
function CityStateSelector({ selectedCities, onChange, onAllStates, onStateChange }) {
  const [selectedState, setSelectedState] = useState("");
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const debounceRef = useRef(null);
  const stateDropdownRef = useRef(null);

  // Close state dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(e.target)) {
        setStateDropdownOpen(false);
        setStateSearch("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const ALL_STATES_OPTION = ["ALL", "All States"];
  const filteredStates = stateSearch.trim()
    ? US_STATES.filter(([abbr, name]) =>
        name.toLowerCase().includes(stateSearch.toLowerCase()) ||
        abbr.toLowerCase().includes(stateSearch.toLowerCase())
      )
    : [ALL_STATES_OPTION, ...US_STATES];

  // Fetch cities when state + query changes
  useEffect(() => {
    if (!selectedState || selectedState === "ALL" || cityQuery.length < 2) {
      setCitySuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setCityLoading(true);
      try {
        const url = `/api/bigquery?action=getCitiesByState&state=${encodeURIComponent(selectedState)}&query=${encodeURIComponent(cityQuery)}`;
        const res = await fetch(url);
        const data = await res.json();
        setCitySuggestions(data.cities || []);
      } catch (err) {
        console.error("City search error:", err);
        setCitySuggestions([]);
      } finally {
        setCityLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [selectedState, cityQuery]);

  function addCity(city, displayCity) {
    const key = `${city.toUpperCase()}|${selectedState}`;
    if (selectedCities.some((c) => c.key === key)) return;
    onChange([...selectedCities, {
      key,
      city: city.toUpperCase(),
      displayCity: titleCase(city),
      state: selectedState,
      label: `${titleCase(city)} · ${selectedState}`,
    }]);
    // Only reset the city search — keep state selected for convenience
    setCityQuery("");
    setCitySuggestions([]);
  }

  function removeCity(key) {
    onChange(selectedCities.filter((c) => c.key !== key));
  }

  return (
    <div className="space-y-3">
      <label className="text-white/50 text-xs block">
        Market <span className="text-gold-400">*</span>
        <span className="text-white/25 ml-2">Select a state and city, or choose All States</span>
      </label>

      {/* State + city search row */}
      <div className="grid grid-cols-2 gap-2">
        {/* Custom state dropdown */}
        <div className="relative" ref={stateDropdownRef}>
          <button
            type="button"
            onClick={() => setStateDropdownOpen((o) => !o)}
            className="w-full flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gold-500/50 transition-colors hover:border-white/20"
          >
            <span className={selectedState ? "text-white" : "text-white/30"}>
              {selectedState === "ALL"
                ? "All States"
                : selectedState
                ? `${selectedState} — ${US_STATES.find(([a]) => a === selectedState)?.[1] || ""}`
                : "Select state…"}
            </span>
            <span className="text-white/30 ml-2">▾</span>
          </button>

          {stateDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-navy-800 border border-white/15 rounded-lg shadow-xl z-30 overflow-hidden">
              {/* Search within states */}
              <div className="p-2 border-b border-white/10">
                <input
                  type="text"
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  placeholder="Search states…"
                  autoFocus
                  className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-gold-500/50"
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filteredStates.map(([abbr, name]) => (
                  <button
                    key={abbr}
                    type="button"
                    onClick={() => {
                      setSelectedState(abbr);
                      setStateDropdownOpen(false);
                      setStateSearch("");
                      setCityQuery("");
                      setCitySuggestions([]);
                      onStateChange?.(abbr);
                      if (abbr === "ALL") {
                        onChange([]);
                        onAllStates?.(true);
                      } else {
                        onAllStates?.(false);
                      }
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors hover:bg-white/5 ${
                      selectedState === abbr ? "text-gold-400 bg-gold-500/5" : "text-white/70"
                    }`}
                  >
                    <span className="font-mono text-white/40 text-xs w-6 shrink-0">{abbr}</span>
                    <span>{name}</span>
                  </button>
                ))}
                {filteredStates.length === 0 && (
                  <p className="text-white/30 text-xs px-3 py-2">No states match</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <input
            type="text"
            value={cityQuery}
            onChange={(e) => setCityQuery(e.target.value)}
            placeholder={
              !selectedState
                ? "Select a state first"
                : selectedState === "ALL"
                ? "Not required for All States"
                : "Type to search cities…"
            }
            disabled={!selectedState || selectedState === "ALL"}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50 disabled:opacity-40"
          />
          {cityLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 text-xs">…</div>
          )}

          {/* Suggestions dropdown */}
          {citySuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-[#0f172a] border border-white/15 rounded-lg shadow-xl z-20 overflow-hidden">
              {citySuggestions.map((s) => (
                <button
                  key={s.city}
                  onClick={() => addCity(s.city, s.displayCity)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-white/80 hover:bg-white/5 transition-colors text-left"
                >
                  <span>{s.displayCity}</span>
                  <span className="text-white/30 text-xs">{s.providerCount.toLocaleString()} providers</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected city tags */}
      {selectedCities.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-white/3 border border-white/10 rounded-lg min-h-10">
          {selectedCities.map((c) => (
            <span
              key={c.key}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-gold-500/15 border border-gold-500/30 rounded-full text-gold-400 text-xs"
            >
              {c.label}
              <button
                onClick={() => removeCity(c.key)}
                className="text-gold-400/60 hover:text-gold-400 transition-colors leading-none"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Two-level specialty picker with tag box ───────────────────────────────────
function SpecialtySelector({ selectedClassifications, onChange }) {
  const [openGroup, setOpenGroup] = useState(null);
  const [search, setSearch] = useState("");

  const filteredGroups = search.trim()
    ? SPECIALTY_GROUPS.map((g) => ({
        ...g,
        classifications: g.classifications.filter((c) =>
          c.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter((g) => g.classifications.length > 0)
    : SPECIALTY_GROUPS;

  function toggleClassification(classification) {
    if (selectedClassifications.includes(classification)) {
      onChange(selectedClassifications.filter((c) => c !== classification));
    } else {
      onChange([...selectedClassifications, classification]);
    }
  }

  function toggleGroup(group) {
    const groupClassifications = SPECIALTY_GROUPS.find((g) => g.group === group)?.classifications || [];
    const allSelected = groupClassifications.every((c) => selectedClassifications.includes(c));
    if (allSelected) {
      onChange(selectedClassifications.filter((c) => !groupClassifications.includes(c)));
    } else {
      const toAdd = groupClassifications.filter((c) => !selectedClassifications.includes(c));
      onChange([...selectedClassifications, ...toAdd]);
    }
  }

  function removeClassification(c) {
    onChange(selectedClassifications.filter((x) => x !== c));
  }

  return (
    <div className="space-y-3">
      <label className="text-white/50 text-xs block">
        Specialty
        <span className="text-white/25 ml-2">Optional — leave blank for all specialties</span>
      </label>

      {/* Search input */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search specialties… e.g. Podiatry, Oncology"
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
      />

      {/* Group list */}
      <div className="border border-white/10 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
        {filteredGroups.map((g) => {
          const groupSelected = g.classifications.filter((c) => selectedClassifications.includes(c)).length;
          const allGroupSelected = groupSelected === g.classifications.length;
          const isOpen = openGroup === g.group || search.trim() !== "";

          return (
            <div key={g.group} className="border-b border-white/5 last:border-0">
              {/* Group header */}
              <button
                onClick={() => setOpenGroup(isOpen && !search ? null : g.group)}
                className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/3 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {/* Group select-all checkbox */}
                  <div
                    onClick={(e) => { e.stopPropagation(); toggleGroup(g.group); }}
                    className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all ${
                      allGroupSelected
                        ? "bg-gold-500 border-gold-500"
                        : groupSelected > 0
                        ? "bg-gold-500/30 border-gold-500/50"
                        : "border-white/20 hover:border-white/40"
                    }`}
                  >
                    {allGroupSelected && <span className="text-navy-900 text-xs font-bold">✓</span>}
                    {groupSelected > 0 && !allGroupSelected && <span className="text-gold-400 text-xs font-bold">–</span>}
                  </div>
                  <span className="text-white/70 text-sm font-medium">{g.group}</span>
                  {groupSelected > 0 && (
                    <span className="text-gold-400 text-xs bg-gold-500/10 px-1.5 py-0.5 rounded-full">
                      {groupSelected}
                    </span>
                  )}
                </div>
                <span className="text-white/25 text-xs">
                  {isOpen ? "▴" : "▾"} {g.classifications.length}
                </span>
              </button>

              {/* Classification list */}
              {isOpen && (
                <div className="bg-white/2 border-t border-white/5">
                  {g.classifications.map((c) => (
                    <button
                      key={c}
                      onClick={() => toggleClassification(c)}
                      className="w-full flex items-center gap-2.5 px-5 py-1.5 hover:bg-white/5 transition-colors text-left"
                    >
                      <div className={`w-3.5 h-3.5 rounded border shrink-0 flex items-center justify-center transition-all ${
                        selectedClassifications.includes(c)
                          ? "bg-gold-500 border-gold-500"
                          : "border-white/20"
                      }`}>
                        {selectedClassifications.includes(c) && (
                          <span className="text-navy-900 text-xs font-bold leading-none">✓</span>
                        )}
                      </div>
                      <span className="text-white/60 text-xs">{c}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected specialty tags */}
      {selectedClassifications.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-white/3 border border-white/10 rounded-lg min-h-10">
          {selectedClassifications.map((c) => {
            const groupEntry = ALL_CLASSIFICATIONS.find((x) => x.classification === c);
            return (
              <span
                key={c}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-500/15 border border-teal-500/30 rounded-full text-teal-400 text-xs"
              >
                {c}
                <button
                  onClick={() => removeClassification(c)}
                  className="text-teal-400/60 hover:text-teal-400 transition-colors leading-none"
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            onClick={() => onChange([])}
            className="text-white/25 hover:text-white/50 text-xs underline transition-colors"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function BuildListPage() {
  const { user, isSignedIn } = useUser();
  const { isPro, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const navigate = useNavigate();

  const [selectedCities, setSelectedCities] = useState([]);
  const [allStatesMode, setAllStatesMode] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [selectedClassifications, setSelectedClassifications] = useState([]);
  const [minPayments, setMinPayments] = useState("");
  const [minOwnership, setMinOwnership] = useState("");
  const [minResearch, setMinResearch] = useState("");
  const [excludedOnly, setExcludedOnly] = useState(false);
  const [podRiskOnly, setPodRiskOnly] = useState(false);
  const [listName, setListName] = useState("");

  const [step, setStep] = useState("filters");
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedList, setSavedList] = useState(null);

  // Auto-generate list name from selected cities
  useEffect(() => {
    if (selectedCities.length > 0 && !listName) {
      const cityNames = selectedCities.slice(0, 2).map((c) => c.displayCity).join(" / ");
      const suffix = selectedClassifications.length === 1 ? ` · ${selectedClassifications[0]}` : "";
      setListName(`${cityNames}${suffix}`);
    }
  }, [selectedCities, selectedClassifications]);

  if (!isSignedIn) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-10">
        <div className="card text-center py-12">
          <p className="text-white/50 text-sm">Sign in to build provider lists.</p>
        </div>
      </main>
    );
  }

  if (!isPro) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-10">
        <div className="card text-center py-12">
          <span className="text-4xl mb-4 block">🔒</span>
          <p className="text-white font-display text-xl mb-2">Build a Provider List</p>
          <p className="text-white/50 text-sm max-w-sm mx-auto mb-6">
            Build custom provider lists from CMS Open Payments and NPPES data to monitor markets and compare groups.
          </p>
          <button
            onClick={() => {
              setUpgradeMessage("Upgrade to Pro to build and compare provider lists.");
              setShowUpgradeModal(true);
            }}
            className="btn-primary px-6 py-2.5"
          >
            Upgrade to Pro
          </button>
        </div>
      </main>
    );
  }

  async function handlePreview() {
    if (!allStatesMode && !selectedState && selectedCities.length === 0) {
      setPreviewError("Select a state, a city, or choose All States.");
      return;
    }
    setPreviewing(true);
    setPreviewError(null);
    setPreviewResult(null);

    try {
      const res = await fetch("/api/build-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "preview",
          cityStatePairs: allStatesMode ? [] : selectedCities.map((c) => ({ city: c.city, state: c.state })),
          allStates: allStatesMode,
          stateOnly: !allStatesMode && selectedCities.length === 0 && !!selectedState,
          stateFilter: (!allStatesMode && selectedCities.length === 0) ? selectedState : null,
          classifications: selectedClassifications,
          minPayments: minPayments ? parseFloat(minPayments) : null,
          minOwnership: minOwnership ? parseFloat(minOwnership) : null,
          minResearch: minResearch ? parseFloat(minResearch) : null,
          excludeOIG: excludedOnly,
          excludeSAM: excludedOnly,
          podRiskOnly: podRiskOnly,
        }),
      });
      const data = await res.json();
      console.error("Preview response:", res.status, data);
      if (!res.ok) throw new Error(data.error || "Preview failed.");
      setPreviewResult(data);
      setStep("preview");
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setPreviewing(false);
    }
  }

  async function handleSave() {
    if (!listName.trim()) { setSaveError("Please enter a list name."); return; }
    setSaving(true);
    setSaveError(null);

    try {
      const res = await apiFetch("/api/build-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save",
          userId: user.id,
          listName: listName.trim(),
          allStates: allStatesMode,
          stateOnly: !allStatesMode && selectedCities.length === 0 && !!selectedState,
          stateFilter: (!allStatesMode && selectedCities.length === 0) ? selectedState : null,
          cityStatePairs: selectedCities.map((c) => ({ city: c.city, state: c.state })),
          classifications: selectedClassifications,
          minPayments: minPayments ? parseFloat(minPayments) : null,
          minOwnership: minOwnership ? parseFloat(minOwnership) : null,
          minResearch: minResearch ? parseFloat(minResearch) : null,
          excludeOIG: excludedOnly,
          excludeSAM: excludedOnly,
          podRiskOnly: podRiskOnly,
          filterCriteria: {
            allStates: allStatesMode,
            stateOnly: !allStatesMode && selectedCities.length === 0 && !!selectedState,
            stateFilter: (!allStatesMode && selectedCities.length === 0) ? selectedState : null,
            cityStatePairs: selectedCities.map((c) => ({ city: c.city, state: c.state, label: c.label })),
            classifications: selectedClassifications,
            minPayments: minPayments || null,
            minOwnership: minOwnership || null,
            minResearch: minResearch || null,
            excludedOnly,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed.");
      setSavedList(data);
      setStep("done");
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function resetFilters() {
    setSelectedCities([]);
    setAllStatesMode(false);
    setSelectedState("");
    setSelectedClassifications([]);
    setMinPayments("");
    setMinOwnership("");
    setMinResearch("");
    setExcludedOnly(false);
    setPodRiskOnly(false);
    setListName("");
    setPreviewResult(null);
    setPreviewError(null);
    setSaveError(null);
    setSavedList(null);
    setStep("filters");
  }

  return (
    <main className="max-w-2xl mx-auto px-4 py-10 space-y-6">
      {/* Header */}
      <div>
        <Link to="/my-providers" className="text-white/40 hover:text-white/70 text-sm transition-colors">
          ← My Providers
        </Link>
        <h1 className="font-display text-3xl text-white mt-2">
          Build a <span className="text-gold-400">Provider List</span>
        </h1>
        <p className="text-white/40 text-sm mt-1">
          Search NPPES and CMS Open Payments data to build a custom provider group or market.
        </p>
      </div>

      {/* Filters card */}
      <div className="card space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-white/70 text-sm font-semibold">Filter Providers</h2>
          {step !== "filters" && (
            <button onClick={() => setStep("filters")} className="text-white/30 hover:text-white/60 text-xs transition-colors">
              ← Edit filters
            </button>
          )}
        </div>

        {/* City-State selector */}
        <CityStateSelector
          selectedCities={selectedCities}
          onChange={(c) => { setSelectedCities(c); setStep("filters"); setPreviewResult(null); }}
          onAllStates={(isAll) => { setAllStatesMode(isAll); setStep("filters"); setPreviewResult(null); }}
          onStateChange={(abbr) => { setSelectedState(abbr); setStep("filters"); setPreviewResult(null); }}
        />

        {/* Specialty selector */}
        <SpecialtySelector selectedClassifications={selectedClassifications} onChange={(c) => { setSelectedClassifications(c); setStep("filters"); setPreviewResult(null); }} />

        {/* Financial filters */}
        <div className="space-y-3">
          <label className="text-white/50 text-xs block">
            Financial Thresholds
            <span className="text-white/25 ml-2">Optional — leave blank to include all providers</span>
          </label>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-white/35 text-xs mb-1 block">Min general payments — 2025 (latest year)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                <input
                  type="number"
                  value={minPayments}
                  onChange={(e) => { setMinPayments(e.target.value); setStep("filters"); setPreviewResult(null); }}
                  placeholder="0"
                  min="0"
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-7 pr-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                />
              </div>
            </div>
            <div>
              <label className="text-white/35 text-xs mb-1 block">Min ownership value — 2025 (latest year)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                <input
                  type="number"
                  value={minOwnership}
                  onChange={(e) => { setMinOwnership(e.target.value); setStep("filters"); setPreviewResult(null); }}
                  placeholder="0"
                  min="0"
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-7 pr-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                />
              </div>
            </div>
            <div>
              <label className="text-white/35 text-xs mb-1 block">Min research payments — 2025 (latest year)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                <input
                  type="number"
                  value={minResearch}
                  onChange={(e) => { setMinResearch(e.target.value); setStep("filters"); setPreviewResult(null); }}
                  placeholder="0"
                  min="0"
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-7 pr-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Exclusion filter */}
        <div className="space-y-3">
          <label className="text-white/50 text-xs block">
            Risk Flags
            <span className="text-white/25 ml-2">Optional — filter by government exclusion lists or POD risk</span>
          </label>
          <button
            type="button"
            onClick={() => { setExcludedOnly((v) => !v); setStep("filters"); setPreviewResult(null); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${
              excludedOnly
                ? "border-red-500/40 bg-red-500/10 text-red-400"
                : "border-white/10 text-white/50 hover:border-white/20 hover:text-white/70"
            }`}
          >
            <div className={`w-4 h-4 rounded border shrink-0 flex items-center justify-center transition-all ${
              excludedOnly ? "bg-red-500 border-red-500" : "border-white/20"
            }`}>
              {excludedOnly && <span className="text-white text-xs font-bold leading-none">✓</span>}
            </div>
            <div>
              <p className="text-sm font-medium">OIG LEIE or SAM.gov excluded only</p>
              <p className="text-xs text-white/30 mt-0.5">Only include providers appearing on the OIG exclusion list, SAM.gov, or both</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => { setPodRiskOnly((v) => !v); setStep("filters"); setPreviewResult(null); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${
              podRiskOnly
                ? "border-blue-500/40 bg-blue-500/10 text-blue-400"
                : "border-white/10 text-white/50 hover:border-white/20 hover:text-white/70"
            }`}
          >
            <div className={`w-4 h-4 rounded border shrink-0 flex items-center justify-center transition-all ${
              podRiskOnly ? "bg-blue-500 border-blue-500" : "border-white/20"
            }`}>
              {podRiskOnly && <span className="text-white text-xs font-bold leading-none">✓</span>}
            </div>
            <div>
              <p className="text-sm font-medium">POD risk only (OIG Work Plan 2026)</p>
              <p className="text-xs text-white/30 mt-0.5">Only include providers flagged as potential Physician Owned Distributorship (POD) risk</p>
            </div>
          </button>
        </div>

        {previewError && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            ✗ {previewError}
          </div>
        )}

        <button
          onClick={handlePreview}
          disabled={previewing || (!allStatesMode && !selectedState && selectedCities.length === 0)}
          className="btn-primary w-full py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {previewing ? "Searching…" : "Preview Results"}
        </button>
      </div>

      {/* Preview card */}
      {step === "preview" && previewResult && (
        <div className="card space-y-5">
          <h2 className="text-white/70 text-sm font-semibold">Preview</h2>

          {previewResult.tooMany ? (
            <div className="bg-gold-500/10 border border-gold-500/20 rounded-lg px-4 py-3 space-y-2">
              <p className="text-gold-400 text-sm font-semibold">
                ⚠ {previewResult.count.toLocaleString()} providers match — limit is 8,000
              </p>
              <p className="text-white/50 text-xs">
                Narrow your search by adding specialty filters or financial thresholds.
              </p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="bg-navy-700/50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="font-display text-3xl text-gold-400">{previewResult.count.toLocaleString()}</div>
                  <div className="text-white/40 text-xs mt-0.5">providers match your filters</div>
                </div>
                <div className="text-right text-xs text-white/30 space-y-0.5">
                  {allStatesMode && <div>All States</div>}
                  {!allStatesMode && selectedState && selectedCities.length === 0 && (
                    <div>{US_STATES.find(([a]) => a === selectedState)?.[1] || selectedState}</div>
                  )}
                  {selectedCities.map((c) => <div key={c.key}>{c.label}</div>)}
                  {selectedClassifications.length > 0 && (
                    <div>{selectedClassifications.length} specialt{selectedClassifications.length === 1 ? "y" : "ies"}</div>
                  )}
                  {minPayments && <div>Min payments {formatCurrency(parseFloat(minPayments))}</div>}
                  {minOwnership && <div>Min ownership {formatCurrency(parseFloat(minOwnership))}</div>}
                  {minResearch && <div>Min research {formatCurrency(parseFloat(minResearch))}</div>}
                  {excludedOnly && <div className="text-red-400/70">OIG LEIE or SAM.gov excluded only</div>}
                  {podRiskOnly && <div className="text-blue-400/70">POD risk only (OIG Work Plan 2026)</div>}
                </div>
              </div>

              {/* Sample table */}
              {previewResult.providers.length > 0 && (
                <div>
                  <p className="text-white/30 text-xs mb-2">
                    Showing {Math.min(50, previewResult.providers.length)} of {previewResult.count.toLocaleString()} · sorted by total payments
                  </p>
                  <div className="overflow-x-auto rounded-xl border border-white/10 max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-white/10">
                          {["Name", "Specialty", "City", "Total Payments"].map((h) => (
                            <th key={h} className="text-left px-3 py-2 text-white/40 font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewResult.providers.map((p, i) => (
                          <tr key={p.npi + i} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                            <td className="px-3 py-2 text-white/80">
                              {p.firstName} {p.lastName}
                              <span className="text-white/25 font-mono ml-1.5 text-xs">{p.npi}</span>
                            </td>
                            <td className="px-3 py-2 text-white/50 max-w-36 truncate">{p.specialty || "—"}</td>
                            <td className="px-3 py-2 text-white/50">{p.city}, {p.state}</td>
                            <td className="px-3 py-2">
                              {p.hasPayments
                                ? <span className="text-gold-400">{formatCurrency(p.totalPaymentsAllYears)}</span>
                                : <span className="text-white/20">$0</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* List name + save */}
              <div className="border-t border-white/10 pt-4 space-y-3">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">List Name</label>
                  <input
                    type="text"
                    value={listName}
                    onChange={(e) => setListName(e.target.value)}
                    placeholder="e.g. Houston Oncologists"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                  />
                </div>

                {saveError && (
                  <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                    ✗ {saveError}
                  </div>
                )}

                <button
                  onClick={handleSave}
                  disabled={saving || !listName.trim()}
                  className="btn-primary w-full py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving
                    ? "Saving list…"
                    : `Save ${previewResult.count.toLocaleString()} providers as "${listName || "My List"}"`}
                </button>
                <p className="text-white/20 text-xs text-center">
                  This will take 2–5 seconds while we pre-compute analytics for your list.
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Done card */}
      {step === "done" && savedList && (
        <div className="card space-y-4">
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-4 space-y-1">
            <p className="text-green-400 font-semibold text-sm">✓ List saved successfully</p>
            <p className="text-white/50 text-xs">
              {savedList.providerCount.toLocaleString()} providers saved to "{savedList.listName}"
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => navigate("/my-providers")} className="btn-primary px-5 py-2 text-sm">
              View in My Providers →
            </button>
            <button onClick={resetFilters} className="text-white/40 hover:text-white/70 text-sm transition-colors">
              Build another list
            </button>
          </div>
        </div>
      )}

      <p className="text-white/20 text-xs text-center">
        Source: NPPES Provider Registry + CMS Open Payments 2016–2025
      </p>
    </main>
  );
}